import _ from 'lodash'
import React, {useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {SelectEntityItem} from 'components/SelectEntityItem'
import {NullAsDefault, renderOwnChild} from 'views/Shared'
import PaginationControls from './PaginationControls'
import {renderDefaultError} from "../LazyPagination";
import useTranslate from "../../../local/useTranslate";
import useAsyncList from "../../useAsyncList";
import {createAsyncAction} from "../../index";
import {getAsynCacheSelector} from "../../selectors";

// asyncId phải deterministic theo page/perPage/query để cache theo từng trang,
// cho phép back lại trang trước đó mà không phải gọi lại API.
export const buildPageAsyncId = (baseId, page, perPage, query) =>
    `${baseId}/page-${page}/per-${perPage}/${JSON.stringify(query || {})}`

/**
 * PagePagination
 * Phân trang kiểu quản trị: click số trang, nhập trang, đổi số item/trang.
 * Khác LazyPagination ở chỗ mỗi lần đổi trang là 1 request MỚI thay thế (không cộng dồn),
 * nhưng vẫn cache theo redux để chuyển qua lại giữa các trang đã xem không phải load lại.
 *
 * Props:
 * - startAction: action khởi tạo cho trang đầu (do PagePaginationWrapper tạo & dispatch sẵn)
 * - apiInfo: {path, method, schema}
 * - query, values: giống LazyPagination
 * - defaultPage, defaultPerPage
 * - pageParam / perPageParam: tên query param BE nhận (mặc định 'page' / 'per_page' theo
 *   đúng field trong response.data.meta.pagination: current_page, per_page, total, total_pages)
 * - refreshKey: đổi giá trị này để reset về trang 1 và xoá cache (vd khi đổi filter)
 * - renderItem(item, index, action): bắt buộc, render từng dòng
 * - renderEmpty, renderLoading, renderError
 * - Header, PaginationMeta: giống LazyPagination (nhận {data, meta})
 * - Wrapper: bọc ngoài toàn bộ nội dung + controls, nhận {count}
 */
export default function PagePagination({
    startAction,
    apiInfo,
    query,
    values,
    refreshKey,
    defaultPage = 1,
    defaultPerPage = 10,
    pageParam = 'page',
    perPageParam = 'per_page',
    pageSizeOptions = [10, 20, 50, 100],
    renderItem = NullAsDefault,
    renderEmpty = () => null,
    renderLoading = () => null,
    renderError = renderDefaultError,
    Header = NullAsDefault,
    PaginationMeta = NullAsDefault,
    Wrapper = renderOwnChild,
}) {
    const t = useTranslate()
    const baseId = apiInfo?.path
    const [page, setPage] = useState(defaultPage)
    const [perPage, setPerPage] = useState(defaultPerPage)
    const dispatch = useDispatch()

    const initialKey = `${defaultPage}-${defaultPerPage}`
    const [map, {set, reset}] = useAsyncList({
        [initialKey]: startAction,
    })

    // Đổi filter (refreshKey) -> xoá cache, quay về trang đầu
    useEffect(() => {
        if (refreshKey === undefined) return
        reset()
        setPage(defaultPage)
        setPerPage(defaultPerPage)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey])

    const key = `${page}-${perPage}`

    const action = useMemo(() => {
        if (map[key]) return map[key]
        return createAsyncAction({
            asyncId: buildPageAsyncId(baseId, page, perPage, query),
            query,
            // page/perPage phải nằm trong `values` vì callApi.js chỉ build search string
            // từ `values[key]` theo `apiInfo.paramsFields` — `query` chỉ dùng để thay
            // path-param dạng `:xxx`, không bao giờ lọt vào query string thực gửi lên BE.
            values: {...values, [pageParam]: page, [perPageParam]: perPage},
            apiInfo: {
                ...apiInfo,
                paramsFields: _.union(apiInfo?.paramsFields, [pageParam, perPageParam]),
            },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, baseId])

    useEffect(() => {
        if (!map[key]) {
            set(key, action)
            dispatch(action)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key])

    // equality theo `updated` giống useDispatchAsyncAction.js -> tránh re-render thừa
    // khi store đổi nhưng không liên quan đến asyncId của trang hiện tại
    const data = useSelector(
        (state) => getAsynCacheSelector(state, action.asyncId),
        (left, right) => _.get(left, 'updated') === _.get(right, 'updated')
    )
    const {success, response, result, isLoading, error} = data || {}

    const schema = _.get(action, 'apiInfo.schema')
    const list = schema ? result || [] : _.get(response, 'data.data') || []
    const pagination = _.get(response, 'data.meta.pagination')
    const total = _.defaultTo(_.get(pagination, 'total'), 0)
    const totalPages = _.defaultTo(_.get(pagination, 'total_pages'), 1)

    const goToPage = (nextPage) => {
        if (nextPage < 1 || nextPage > totalPages || nextPage === page) return
        setPage(nextPage)
    }
    const changePerPage = (nextPerPage) => {
        if (nextPerPage === perPage) return
        setPerPage(nextPerPage)
        setPage(1)
    }
    const retry = () => {
        set(key, undefined)
        dispatch(action)
    }

    if (error) {
        return renderError(
            <div className="flex flex-col flex-center gap-2 py-4">
                <button
                    onClick={retry}
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm">
                    {t('retry')}
                </button>
            </div>,
            error
        )
    }

    if (!success) {
        return isLoading ? renderLoading() : null
    }

    if (!list.length) {
        return renderEmpty()
    }

    return <>
        <Wrapper count={list.length}>
            <Header data={response} meta={<PaginationMeta data={response} />} />
            {list.map((item, i) =>
                schema ? (
                    <SelectEntityItem key={i} item={item} schema={schema}>
                        {(entity) => renderItem(entity, i, action)}
                    </SelectEntityItem>
                ) : (
                    renderItem(item, i, action)
                )
            )}
        </Wrapper>
        <PaginationControls
            page={page}
            totalPages={totalPages}
            total={total}
            perPage={perPage}
            pageSizeOptions={pageSizeOptions}
            loading={!!isLoading}
            onChangePage={goToPage}
            onChangePerPage={changePerPage}
        />
    </>
}
